import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Card, Container, Typography, Button } from '@mui/material';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import toast from 'react-hot-toast';
import { Fields } from '../../../ui-component/FormikFormLibrary/FormikTypes';
import { httpRequest } from 'src/services/httpRequest';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch, useSelector } from 'react-redux';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { RootState } from 'src/redux-store/appStore';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  PaymentApplicationDetails,
  PaymentSummary
} from 'src/pages/Payment/types';
import { fileUpload } from '../common/uploadPDF';
import PetApplicationFeeWithFormik from 'src/pages/Permits/PetPermit/PetApplicationFeeWithFormik';
import PetSelection from './PetSelection';
import ApplicationFormLayout from '../common/ApplicationFormLayout';
import WarningIcon from '@mui/icons-material/Warning';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DownloadPopUp from 'src/ui-component/DownloadApplicationForm/DownloadPopUp';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { calculatePetLicenseFee } from 'src/components/common/calculatePetFee';
import {
  US_STATE_CODES,
  combineOwnerAddress,
  parseOwnerAddress
} from 'src/utils/usStates';
import { getFormattedPhoneNumber } from 'src/utils/CommonFunctions';

/** Yup mobile rule expects 12-char XXX-XXX-XXXX (see ValidationSchema). */
const formatOwnerPhoneInitial = (raw: string | undefined | null): string => {
  if (raw == null || String(raw).trim() === '') return '';
  return getFormattedPhoneNumber(String(raw));
};

// Define types
interface ApplicationDetails {
  isStatus: string;
  ApplicationType: string;
  ApplicantName: string;
  OwnerName: string;
  OwnerAddress: string;
  OwnerContactNumber: string;
  OwnerEmailAddress: string;
  RabiesIssueDate: string;
  RabiesExpirationDate: string;
  VetClinic: string;
  VetPhoneNumber: string;
  DocumentFileUpload?: File;
  DocumentURL?: string; 
  InspectionFee?: number;
  LateFeesWaived?: boolean;
  ApplicationDate?: string; // Application date (editable only for Manager/Admin)
  [key: string]: string | number | boolean | File | undefined; // Allow for dynamic properties
}

interface FeeDetails {
  applicationFee: number;
  [key: string]: number;
}

interface SubmissionResponse {
  status: string;
  message?: string;
  data?: {
    ApplicationID: string;
    [key: string]: string | number | boolean | null;
  };
  type?: string;
}

interface RouteParams {
  townshipId?: string;
  department?: string;
  permit?: string;
  [key: string]: string | undefined;
}

type SubmissionType = 'payment' | 'draft' | 'default' | 'submit';

/** Latest license row from API: LicenseStatus and/or DB ApplicationStatus may be Expired. */
const isPetLicenseExpired = (pet: {
  applications?: { LicenseStatus?: string; ApplicationStatus?: string }[];
} | null) => {
  const app = pet?.applications?.[0];
  const license = typeof app?.LicenseStatus === 'string' ? app.LicenseStatus.trim().toLowerCase() : '';
  const row = typeof app?.ApplicationStatus === 'string' ? app.ApplicationStatus.trim().toLowerCase() : '';
  return license === 'expired' || row === 'expired';
};

const NewApplication: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { townshipId, permit, department } = useParams<RouteParams>();
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const currentPermitRole = useSelector(
    (state: RootState) =>
      state?.currentPermit?.currentPermitDetails?.currentPermitUserRole
  );
  
  // Check if user is Manager or Admin (for optional document upload)
  const isManagerOrAdmin = 
    currentPermitRole === 'Manager' || 
    userData?.UserRole === 'Admin' || 
    userData?.UserRole === 'Super Admin';
  const loggedInOwnerName = `${userData?.FirstName ?? ''} ${userData?.LastName ?? ''}`.trim();
  const loggedInOwnerEmail = String(userData?.EmailID ?? '').trim();

  // State management
  const [loading, setLoading] = useState<boolean>(false);
  const [clickedOn, setClickedOn] = useState<string>('');
  const [petDetails, setPetDetails] = useState(null);
  /** Bumps when user returns from My Pets after adding a pet so the list refetches */
  const [petListVersion, setPetListVersion] = useState(0);
  const [priorAppPrefill, setPriorAppPrefill] = useState<{
    line1: string;
    city: string;
    state: string;
    zip: string;
    ownerPhone: string;
    ownerEmail: string;
  } | null>(null);
  const [priorAppPrefillLoading, setPriorAppPrefillLoading] =
    useState<boolean>(false);

  const renewData = location?.state
    ? (location.state as { item: any }).item
    : null;

  const [formState, setFormState] = useState<{
    taxRate: number;
    feeDetails: FeeDetails;
  }>({
    taxRate: 0,
    feeDetails: { applicationFee: 0 }
  });

  // When a pet is chosen on a new (non-renewal) application, load city/state/zip from their latest license application if any.
  useEffect(() => {
    const ownerName = String(petDetails?.OwnerName ?? '').trim();
    if (renewData || !petDetails?.UserID || !ownerName) {
      setPriorAppPrefill(null);
      setPriorAppPrefillLoading(false);
      return;
    }
    let cancelled = false;
    setPriorAppPrefillLoading(true);
    (async () => {
      try {
        const qs = new URLSearchParams({
          ownerName,
          petOwnerUserId: String(petDetails.UserID)
        });
        const res = await httpRequest(
          'GET',
          `/pet/pet_application/latest-address-by-owner?${qs.toString()}`
        );
        if (cancelled) return;
        if (res?.status === 'success' && res?.data) {
          setPriorAppPrefill({
            line1: String(res.data.OwnerAddressLine1 ?? ''),
            city: String(res.data.OwnerCity ?? ''),
            state: String(res.data.OwnerState ?? ''),
            zip: String(res.data.OwnerZipCode ?? ''),
            ownerPhone: String(res.data.OwnerContactNumber ?? ''),
            ownerEmail: String(res.data.OwnerEmailAddress ?? '')
          });
        } else {
          setPriorAppPrefill(null);
        }
      } catch {
        if (!cancelled) setPriorAppPrefill(null);
      } finally {
        if (!cancelled) setPriorAppPrefillLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [petDetails?.UserID, petDetails?.OwnerName, renewData]);

  const isRenewFromExpiredPet = useMemo(() => {
    if (renewData) return false;
    return isPetLicenseExpired(petDetails);
  }, [petDetails, renewData]);

  const isRenewalApplicationFlow = Boolean(renewData || isRenewFromExpiredPet);

  useEffect(() => {
    const refresh = (location.state as { refreshPetList?: boolean } | null)?.refreshPetList;
    if (refresh) {
      setPetListVersion((v) => v + 1);
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  const formButtonTitles = useMemo(
    () =>
      isRenewFromExpiredPet
        ? (['Cancel', 'Renew'] as const)
        : (['Cancel', 'Save Draft', 'Submit'] as const),
    [isRenewFromExpiredPet]
  );

  const initialApplicationDetails: ApplicationDetails = {
    ApplicationType: '',
    ApplicantName: userData?.FirstName + ' ' + userData.LastName,
    OwnerAddress: '',
    OwnerContactNumber: '',
    OwnerEmailAddress: '',
    OwnerName: '',
    RabiesIssueDate: '',
    RabiesExpirationDate: '',
    VetClinic: '',
    VetPhoneNumber: '',
    isStatus: '',
    LateFeesWaived: false,
    ApplicationDate: new Date().toISOString().split('T')[0] // Default to today's date (YYYY-MM-DD format)
  };

  // Navigation helpers - memoized to prevent unnecessary re-renders
  const navigateTo = useMemo(
    () => ({
      dashboard: () => navigate(`/dashboard/${permit}`),
      draftApplications: () =>
        navigate(
          `/${townshipId}/Department of Health or Animal Control/${permit}/draft-application`
        ),
      applicationList: () =>
        navigate(
          `/${townshipId}/Department of Health or Animal Control/${permit}/application-list`
        ),
      payment: (paymentData: {
        paymentSummary: PaymentSummary;
        paymentApplicationDetails: PaymentApplicationDetails;
      }) => navigate('/payment-methods', { state: paymentData })
    }),
    [navigate, permit, townshipId]
  );

  // Handle form submission errors
  const handleSubmissionError = useCallback(
    (error: { message?: string }) => {
      setLoading(false);
      const errorMessage = error?.message || 'An unknown error occurred';
      toast.error(errorMessage);
      dispatch(handleError(errorMessage));
    },
    [dispatch]
  );

  // Handle successful submission based on type
  const handleSuccessfulSubmission = useCallback(
    async (response: SubmissionResponse, submissionType: SubmissionType) => {
      switch (submissionType) {
        case 'payment':
          setLoading(false);
          if (response.data) {
            const paymentSummary: PaymentSummary = {
              fees: Object.fromEntries(
                Object.entries(formState.feeDetails).filter(
                  ([, value]) => value > 0
                )
              ),
              ...(formState.taxRate > 0 ? { taxRate: formState.taxRate } : {})
            };

            const paymentApplicationDetails: PaymentApplicationDetails = {
              applicationDetails: {
                userName: `${userData?.FirstName} ${userData?.LastName}`,
                applicationNumber: null,
                applicationId: response.data.ApplicationID,
                applicationStatus: 'Draft',
                statusPayload: 'In Review',
                permitPrefix: 'Pet'
              }
            };

            navigateTo.payment({ paymentSummary, paymentApplicationDetails });
          }
          break;
        case 'draft':
          setLoading(false);
          toast.success('Application saved as draft successfully.');
          navigateTo.draftApplications();
          break;
        case 'submit':
        default:
          setLoading(false);
          toast.success(
            isRenewalApplicationFlow
              ? 'Renewal application submitted successfully.'
              : 'Application submitted successfully.'
          );
          navigateTo.applicationList();
          break;
      }
    },
    [
      formState.feeDetails,
      formState.taxRate,
      userData,
      navigateTo,
      permit,
      dispatch,
      isRenewalApplicationFlow
    ]
  );

  // Consolidated submission handler
  const handleSubmission = useCallback(
    async (data: ApplicationDetails, submissionType: SubmissionType) => {
      try {
        setLoading(true);
        // Data processing

        const finalFee = data.IsSpayedNeutered === 'Yes' ? formState.feeDetails.applicationFeeSpayed : formState.feeDetails.applicationNonFeeSpayed
        // Create processedData without DocumentFileUpload (File type) - will add as string below
        const { DocumentFileUpload, ...dataWithoutFile } = data;
        
        // Use ApplicationDate for fee calculation (defaults to today if not provided)
        const applicationDate = data.ApplicationDate 
          ? new Date(data.ApplicationDate) 
          : new Date();
        
        // Calculate application fee - if LateFeesWaived is true, only return base fee
        const calculatedFee = data.LateFeesWaived 
          ? finalFee || 0  // If late fees waived, only base fee
          : calculatePetLicenseFee(
              finalFee || 0,
              formState.feeDetails.lateMonthlyFee || 0,
              formState.feeDetails.delinquencyFee || 0,
              applicationDate,
              data.IsSpayedNeutered === 'Yes'
            );
        
        // Convert MM-DD-YYYY to YYYY-MM-DD or ISO for backend compatibility
        const toDateString = (val: string | undefined) => {
          if (!val || !val.trim()) return null;
          const parts = val.trim().split('-');
          if (parts.length === 3) {
            const [a, b, c] = parts;
            if (a.length === 4) return val; // Already YYYY-MM-DD
            if (c.length === 4) return `${c}-${a}-${b}`; // MM-DD-YYYY -> YYYY-MM-DD
          }
          return val;
        };

        const processedData: any = {
          ...dataWithoutFile,
          IsSpayedNeutered: data.IsSpayedNeutered === 'Yes',
          ApplicationFee: calculatedFee,
          PilotClinicFee: formState.feeDetails.pilotClinicFeePet ?? 0,
          StateRegistrationFee: formState.feeDetails.stateRegistrationFeePet ?? 0,
          LateFeesWaived: data.LateFeesWaived || false,
          ApplicationDate: toDateString(data.ApplicationDate) || new Date().toISOString().split('T')[0],
        };

        if (processedData.RabiesExpirationDate?.trim()) {
          processedData.RabiesExpirationDate = toDateString(processedData.RabiesExpirationDate) || processedData.RabiesExpirationDate;
        }
        if (!data.RabiesIssueDate || data.RabiesIssueDate.trim() === '') {
          processedData.RabiesIssueDate = null;
        } else {
          processedData.RabiesIssueDate = toDateString(data.RabiesIssueDate) || data.RabiesIssueDate;
        }
        // Keep OwnerEmailAddress as empty string instead of null to avoid database constraint issues
        if (!data.OwnerEmailAddress || data.OwnerEmailAddress.trim() === '') {
          processedData.OwnerEmailAddress = '';
        }
        
        // Remove optional string fields if empty (instead of setting to null)
        if (!data.VetClinic || data.VetClinic.trim() === '') {
          delete processedData.VetClinic;
        }
        if (!data.VetPhoneNumber || data.VetPhoneNumber.trim() === '') {
          delete processedData.VetPhoneNumber;
        }

        if (formState.feeDetails.inspectionFee)
          processedData.InspectionFee = formState.feeDetails.inspectionFee;

        // Handle file upload if available (optional)
        if (data.DocumentFileUpload) {
          try {
            const document = await fileUpload(data.DocumentFileUpload, 'Pet');
            processedData.DocumentFileUpload = document.fileName;
            processedData.DocumentURL = document.documentURL;
          } catch (uploadError) {
            handleSubmissionError({
              message: 'File upload failed. Please try again.'
            });
            return;
          }
        } else {
          // If no document uploaded, set to empty string for backend compatibility
          // Backend validator allows empty/null, and controller handles it gracefully
          processedData.DocumentFileUpload = '';
          // Don't include DocumentURL if no document uploaded (backend handles missing gracefully)
          // processedData.DocumentURL will be undefined, which is fine
        }

        processedData.ApplicationType =
          renewData || isRenewFromExpiredPet ? 'Renew' : 'New';
        processedData.isStatus =
          submissionType === 'draft' ? 'Draft' : 'In Review';

        // Submit data
        const response: SubmissionResponse = await httpRequest(
          'POST',
          '/pet/pet_application/insert',
          processedData
        );

        if (response?.status === 'success') {
          handleSuccessfulSubmission(response, submissionType);
        } else if (response?.type === 'NETWORK_ERROR') {
          handleSubmissionError({
            message:
              'Network error. Please check your connection and try again.'
          });
        } else {
          handleSubmissionError(response);
        }
      } catch (error) {
        handleSubmissionError(error);
      }
    },
    [
      formState.feeDetails,
      handleSubmissionError,
      handleSuccessfulSubmission,
      renewData,
      isRenewFromExpiredPet
    ]
  );
  // Form submission handler
  const handleSubmit = useCallback(
    async (data: ApplicationDetails) => {
      if (petDetails === null) {
        toast.error('Please select a pet to proceed.');
        return;
      }
      const combinedAddress = combineOwnerAddress(
        (data as any).OwnerAddressLine1,
        (data as any).OwnerCity,
        (data as any).OwnerState,
        (data as any).OwnerZipCode
      );
      const {
        OwnerAddressLine1,
        OwnerCity,
        OwnerState,
        OwnerZipCode,
        ...dataWithoutSubAddressFields
      } = data as any;
      const payload = {
         ...dataWithoutSubAddressFields,
        OwnerAddress: combinedAddress,
        PetType: petDetails?.PetType,
        PetName: petDetails?.PetName,
        PetDOB: petDetails?.PetDOB ?? null,
        PetBreed: petDetails?.PetBreed,
        PetColor: petDetails?.PetColor,
        PetHair: petDetails?.PetHair,
        PetWeight: petDetails?.PetWeight,
        ChipNumber: petDetails?.ChipNumber,
        PetSex: petDetails?.PetSex,
        IsSpayedNeutered: petDetails?.IsSpayedNeutered,
        PetID: petDetails?.PetID
      };

      if (clickedOn === 'Proceed to Payment') {
        await handleSubmission(payload, 'payment');
      } else if (clickedOn === 'Save Draft') {
        await handleSubmission(payload, 'draft');
      } else if (clickedOn === 'Submit' || clickedOn === 'Renew') {
        await handleSubmission(payload, 'submit');
      } else {
        await handleSubmission(payload, 'default');
      }
    },
    [clickedOn, handleSubmission, petDetails]
  );

  // Handle tax rate change
  const setTaxRate = useCallback((taxRate: number) => {
    setFormState((prev) => ({ ...prev, taxRate }));
  }, []);

  // Handle fee details change
  const setFeeDetails = useCallback((feeDetails: FeeDetails) => {
    setFormState((prev) => ({ ...prev, feeDetails }));
  }, []);

  // Cancel button handler
  const handleCancel = useCallback(() => {
    navigateTo.dashboard();
  }, [navigateTo]);

  // Group form fields by sections for better maintainability
  const formFields = useMemo(() => {
    /** Shimmers address rows while latest-address-by-owner resolves (new application only). */
    const ownerAddressPrefetching =
      priorAppPrefillLoading && !renewData;

    // Owner information fields
    const ownerFields: Fields[] = [
      {
        fieldName: 'ApplicationDate',
        fieldLabel: 'Application Date',
        fieldType: 'pastDate',
        required: true,
        disabled: !isManagerOrAdmin, // Only editable for Manager/Admin
        initialValue:
          initialApplicationDetails.ApplicationDate || 
          renewData?.CreateDate?.split('T')[0] || 
          new Date().toISOString().split('T')[0],
        heading: 'Applicant/Owner Information'
      },
      {
        fieldName: 'ApplicantName',
        fieldLabel: 'Applicant Name',
        fieldType: 'text',
        required: true,
        disabled: true,
        initialValue:
          initialApplicationDetails.ApplicantName || renewData?.ApplicantName
      },
      {
        fieldName: 'OwnerName',
        fieldLabel: 'Owner Name',
        fieldType: 'text',
        required: true,
        initialValue:
          renewData?.OwnerName ||
          (!isManagerOrAdmin ? loggedInOwnerName : '') ||
          petDetails?.OwnerName ||
          initialApplicationDetails.OwnerName ||
          ''
      },
      {
        fieldName: 'OwnerContactNumber',
        fieldLabel: 'Owner Contact Number',
        fieldType: 'mobile',
        required: true,
        initialValue: formatOwnerPhoneInitial(
          renewData?.OwnerContactNumber ||
            petDetails?.OwnerContactNumber ||
            priorAppPrefill?.ownerPhone ||
            initialApplicationDetails.OwnerContactNumber ||
            ''
        )
      },
      {
        fieldName: 'OwnerEmailAddress',
        fieldLabel: 'Owner Email Address',
        fieldType: 'email',
        required: false,
        initialValue:
          renewData?.OwnerEmailAddress ||
          (!isManagerOrAdmin ? loggedInOwnerEmail : '') ||
          petDetails?.OwnerEmail ||
          priorAppPrefill?.ownerEmail ||
          initialApplicationDetails.OwnerEmailAddress ||
          ''
      },
     {
        fieldName: 'OwnerAddressLine1',
        fieldLabel: 'Owner Address',
        fieldType: 'text',
        required: true,
        loading: ownerAddressPrefetching,
        initialValue: renewData?.OwnerAddress
          ? parseOwnerAddress(renewData.OwnerAddress).line1
          : priorAppPrefill?.line1 ?? ''
      },
      {
        fieldName: 'OwnerCity',
        fieldLabel: 'City',
        fieldType: 'text',
        required: true,
        loading: ownerAddressPrefetching,
        initialValue: renewData?.OwnerAddress
          ? parseOwnerAddress(renewData.OwnerAddress).city
          : priorAppPrefill?.city ?? ''
      },
      {
        fieldName: 'OwnerState',
        fieldLabel: 'State',
        fieldType: 'select',
        required: true,
        options: US_STATE_CODES,
        loading: ownerAddressPrefetching,
        initialValue: renewData?.OwnerAddress
          ? parseOwnerAddress(renewData.OwnerAddress).state
          : priorAppPrefill?.state ?? ''
      },
      {
        fieldName: 'OwnerZipCode',
        fieldLabel: 'Zip Code',
        fieldType: 'text',
        required: true,
        lengthLimit: 10,
        loading: ownerAddressPrefetching,
        initialValue: renewData?.OwnerAddress
          ? parseOwnerAddress(renewData.OwnerAddress).zip
          : priorAppPrefill?.zip ?? ''
      }
    ];

    // Veterinary information fields
    const vetFields: Fields[] = [
      {
        fieldName: 'RabiesIssueDate',
        fieldLabel: 'Rabies Issue Date',
        fieldType: 'pastDate',
        required: false,
        initialValue:
          initialApplicationDetails.RabiesIssueDate ||
          renewData?.RabiesIssueDate
      },
      {
        fieldName: 'RabiesExpirationDate',
        fieldLabel: 'Rabies Expiration Date',
        fieldType: 'futureDate',
        required: true,
        initialValue:
          initialApplicationDetails.RabiesExpirationDate ||
          renewData?.RabiesExpirationDate
      },
      {
        fieldName: 'VetClinic',
        fieldLabel: 'Vet Clinic',
        fieldType: 'text',
        required: false,
        initialValue:
          initialApplicationDetails.VetClinic || renewData?.VetClinic
      },
      {
        fieldName: 'VetPhoneNumber',
        fieldLabel: 'Vet Phone Number',
        fieldType: 'mobile',
        required: false,
        initialValue: formatOwnerPhoneInitial(
          initialApplicationDetails.VetPhoneNumber || renewData?.VetPhoneNumber || ''
        )
      },
      // Late fees waiver checkbox - only for Manager/Admin, placed on the right side of VetPhoneNumber
      ...(isManagerOrAdmin
        ? ([
            {
              fieldName: 'LateFeesWaived',
              fieldLabel: 'Waive Late Fees',
              fieldType: 'check' as const,
              required: false
            }
          ] as Fields[])
        : [])
    ];

    // Document upload fields - Rabies Certificate Upload is optional
    const documentFields: Fields[] = [
      {
        fieldName: 'DocumentFileUpload',
        fieldLabel: 'Upload Document',
        fieldType: 'file',
        isErrorField: false,
        required: false, // Optional: Rabies Certificate Upload
        listUI: true,
        placement: 'start',
        multiSelect: false,
        disabled: false,
        aboveParagraph: (
          <Typography>
            Please upload scanned copy or photo of the rabies vaccination
            certificate.
            <br /> Only image files (JPG, PNG, GIF, SVG, HEIC) and PDF files are
            allowed, with maximum file size of 10MB.
          </Typography>
        )
      }
    ];

    // Combine all field groups
    return [...ownerFields, ...vetFields, ...documentFields];
  }, [
    initialApplicationDetails,
    isManagerOrAdmin,
    petDetails,
    renewData,
    priorAppPrefill,
    loggedInOwnerName,
    loggedInOwnerEmail,
    priorAppPrefillLoading
  ]);

  return loading ? (
    <LoadingOverlay />
  ) : (
    <Container sx={{ mt: 2 }} className="main-table-container">
      <Breadcrumb PermitName={'Pet License'} PageTitle={'New Application'} />
      <FormHeaderGreeting
        name={`${userData?.FirstName} ${userData?.LastName}`}
        applicationType={'new'}
      />
      <Card>
        <Box
          m={{ md: 3, xs: 1 }}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 2 }
          }}
        >
          <Typography variant="h4">
            {isRenewalApplicationFlow ? 'Renewal Application' : 'New Application'}
          </Typography>
          <DownloadPopUp
            title={'PET LICENSE APPLICATION'}
            ApplicationDetails={{
              ApplicantName: ',',
              OwnerName: '',
              OwnerContactNumber: '',
              OwnerEmailAddress: '',
              OwnerAddress: '',
              PetType: '',
              PetName: '',
              PetBreed: '',
              PetDateOfBirth: '',
              PetWeight: '',
              PetHair: '',
              PetColor: '',
              PetSex: '',
              IsSpayedNeutered: '',
              ChipNumber: '',
              RabiesIssueDate: '',
              RabiesExpirationDate: '',
              VetClinic: '',
              VetPhoneNumber: ''
            }}
          />
        </Box>

        <ApplicationFormLayout
          sidebar={
            <Box display="flex" flexDirection="column" sx={{ minWidth: 0 }}>
              {renewData && (
                <Box
                  mb={3}
                  p={2}
                  bgcolor="#FFF4E5"
                  borderRadius={1}
                  border="1px solid #FFB74D"
                  display="flex"
                  alignItems="flex-start"
                >
                  <WarningIcon color="warning" sx={{ mr: 1, mt: 0.25 }} fontSize="small" />
                  <Typography variant="body2" color="warning.dark">
                    Before proceeding with the renewal, kindly update pet details
                    in the &apos;My Pets&apos; section if applicable.
                  </Typography>
                </Box>
              )}

              {!renewData && isRenewFromExpiredPet && (
                <Box
                  mb={3}
                  p={2}
                  bgcolor="#E3F2FD"
                  borderRadius={1}
                  border="1px solid #64B5F6"
                  display="flex"
                  alignItems="flex-start"
                >
                  <InfoOutlinedIcon color="info" sx={{ mr: 1, mt: 0.25 }} fontSize="small" />
                  <Typography variant="body2" color="info.dark">
                    This pet&apos;s license has expired. Use <strong>Renew</strong> to
                    submit a renewal for manager review and payment (same steps as a new
                    submission).
                  </Typography>
                </Box>
              )}

              {!renewData && department && townshipId && permit && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() =>
                      navigate(`/${townshipId}/${department}/${permit}/my-pets`, {
                        state: {
                          openAddPetFromNewApplication: true,
                          returnToNewApplication: `${location.pathname}${location.search ?? ''}`
                        }
                      })
                    }
                  >
                    Add pet
                  </Button>
                </Box>
              )}

              <PetSelection
                petDetails={petDetails}
                setPetDetails={setPetDetails}
                editData={renewData || null}
                petListVersion={petListVersion}
              />
            </Box>
          }
          form={
            <FormikForm
              allFields={formFields}
              buttonPlacement={'right'}
              buttonTitle={[...formButtonTitles]}
              setClickedOn={setClickedOn}
              clickProp
              content={
                <PetApplicationFeeWithFormik
                  setTaxRate={setTaxRate}
                  setFeeDetails={setFeeDetails}
                  feeDetails={formState.feeDetails}
                  isSpayedNeutered={petDetails?.IsSpayedNeutered}
                />
              }
              formMethod={handleSubmit}
              heading={'Pet Permit'}
              handleOtherButton={handleCancel}
            />
          }
        />
      </Card>
    </Container>
  );
};

export default NewApplication;
